import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';
import RecordRTC from 'recordrtc';

const MIN_RECORDING_SECONDS = 5;
const NUM_EQ_BARS = 5;
const EQ_WIDTH = 40;
const EQ_HEIGHT = 32;
const BAR_WIDTH = 5;
const BAR_GAP = 3;

class AudioRecorder extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isPreparing: false,
            isRecording: false,
            isTranscribing: false,
            isRefining: false,
            refineStatus: null, // 'refining', 'refined', 'filtered', 'failed'
            error: null,
            recordingTime: 0,
            availableDevices: [],
            selectedDeviceId: null,
            showDeviceSelector: false,
        };
        this.recorder = null;
        this.recordingTimer = null;
        this.audioContext = null;
        this.analyser = null;
        this.animationFrame = null;
        this.maxAudioLevel = 0;
        this.canvasRef = null;
        // Undo support: track the last dictation so the user can revert
        // a refinement back to the raw Whisper transcription.
        this.lastRawText = null;
        this.lastRefinedText = null;
        this.refineStatusTimer = null;
    }

    loadDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');

            const savedDeviceId = localStorage.getItem('audioRecorder_selectedDeviceId');
            let selectedDeviceId = audioInputs[0]?.deviceId || null;

            if (savedDeviceId && audioInputs.some(d => d.deviceId === savedDeviceId)) {
                selectedDeviceId = savedDeviceId;
            }

            this.setState({
                availableDevices: audioInputs,
                selectedDeviceId: selectedDeviceId
            });
        } catch (error) {
            console.error('Error loading devices:', error);
        }
    }

    componentDidMount() {
        this.loadDevices();
    }

    drawEqBars = (bars) => {
        const canvas = this.canvasRef;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, EQ_WIDTH, EQ_HEIGHT);

        for (let i = 0; i < bars.length; i++) {
            const level = bars[i] || 0;
            const barHeight = Math.max(4, (level / 100) * EQ_HEIGHT);
            const x = i * (BAR_WIDTH + BAR_GAP);
            const y = EQ_HEIGHT - barHeight;

            if (level > 50) {
                ctx.fillStyle = '#dc3545';
            } else if (level > 20) {
                ctx.fillStyle = '#ffc107';
            } else {
                ctx.fillStyle = '#adb5bd';
            }

            // Rounded rect
            const radius = 2;
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + BAR_WIDTH - radius, y);
            ctx.quadraticCurveTo(x + BAR_WIDTH, y, x + BAR_WIDTH, y + radius);
            ctx.lineTo(x + BAR_WIDTH, y + barHeight - radius);
            ctx.quadraticCurveTo(x + BAR_WIDTH, y + barHeight, x + BAR_WIDTH - radius, y + barHeight);
            ctx.lineTo(x + radius, y + barHeight);
            ctx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
            ctx.fill();
        }
    }

    startAudioMonitoring = () => {
        if (!this.analyser) return;

        const freqBinCount = this.analyser.frequencyBinCount;
        const frequencyData = new Uint8Array(freqBinCount);

        const draw = () => {
            if (!this.state.isRecording || !this.analyser) return;

            this.analyser.getByteFrequencyData(frequencyData);

            // Voice frequency bands (~100Hz - 3500Hz)
            // fftSize=512 at 44100Hz: each bin ≈ 86Hz
            const bandRanges = [
                [1, 5],    // ~86-430Hz  (low voice)
                [5, 10],   // ~430-860Hz (mid-low)
                [10, 18],  // ~860-1550Hz (mid)
                [18, 28],  // ~1550-2410Hz (mid-high)
                [28, 42],  // ~2410-3610Hz (high/sibilance)
            ];
            const bars = [];
            for (let i = 0; i < NUM_EQ_BARS; i++) {
                const [start, end] = bandRanges[i];
                let peak = 0;
                for (let j = start; j < end && j < freqBinCount; j++) {
                    if (frequencyData[j] > peak) peak = frequencyData[j];
                }
                bars.push(Math.min(100, Math.round((peak / 140) * 100)));
            }

            this.drawEqBars(bars);

            // Track max level
            const avg = bars.reduce((a, b) => a + b, 0) / bars.length;
            if (avg > this.maxAudioLevel) {
                this.maxAudioLevel = avg;
            }

            this.animationFrame = requestAnimationFrame(draw);
        };

        this.animationFrame = requestAnimationFrame(draw);
    }

    getAudioStream = async () => {
        const { selectedDeviceId } = this.state;

        const audioConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
        };

        if (selectedDeviceId) {
            audioConstraints.deviceId = { exact: selectedDeviceId };
        }

        return navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    }

    checkMicHasSignal = (analyser) => {
        // Check if analyser is receiving any non-silence audio
        const dataArray = new Uint8Array(analyser.fftSize);
        analyser.getByteTimeDomainData(dataArray);

        let maxDeviation = 0;
        for (let i = 0; i < dataArray.length; i++) {
            const deviation = Math.abs(dataArray[i] - 128);
            if (deviation > maxDeviation) maxDeviation = deviation;
        }
        return maxDeviation > 1;
    }

    waitForMicSignal = (analyser, timeoutMs = 2000) => {
        return new Promise(resolve => {
            const startTime = Date.now();
            const check = () => {
                if (this.checkMicHasSignal(analyser)) {
                    resolve(true);
                } else if (Date.now() - startTime > timeoutMs) {
                    resolve(false);
                } else {
                    setTimeout(check, 50);
                }
            };
            check();
        });
    }

    setupAnalyser = async (stream) => {
        // Clean up previous audio context if any
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
            this.analyser = null;
        }

        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
        const source = this.audioContext.createMediaStreamSource(stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 512;
        this.analyser.smoothingTimeConstant = 0.4;
        this.analyser.minDecibels = -90;
        this.analyser.maxDecibels = -10;
        source.connect(this.analyser);

        return this.analyser;
    }

    startRecording = async () => {
        try {
            // Starting a new dictation invalidates any pending undo
            this.clearUndo();
            this.setState({ isPreparing: true, error: null });

            // Get microphone stream
            let stream = await this.getAudioStream();
            await this.loadDevices();

            // Set up analyser and check if mic is actually producing audio.
            // On macOS, the first getUserMedia after page load can return a
            // "dead" stream where the permission is granted but the audio
            // hardware hasn't activated yet. The fix: detect this and get
            // a fresh stream — the second call always works.
            let analyser = await this.setupAnalyser(stream);
            let hasSignal = await this.waitForMicSignal(analyser, 500);

            if (!hasSignal) {
                // First stream is dead — release it and get a new one
                stream.getTracks().forEach(track => track.stop());
                if (this.audioContext) {
                    this.audioContext.close();
                    this.audioContext = null;
                    this.analyser = null;
                }

                stream = await this.getAudioStream();
                analyser = await this.setupAnalyser(stream);
                await this.waitForMicSignal(analyser, 1500);
            }

            this.maxAudioLevel = 0;

            this.recorder = new RecordRTC(stream, {
                type: 'audio',
                mimeType: 'audio/wav',
                recorderType: RecordRTC.StereoAudioRecorder,
                desiredSampRate: 16000,
                numberOfAudioChannels: 1,
            });

            this.recorder.startRecording();

            // Set recording state, then start canvas animation after DOM renders
            this.setState({ isPreparing: false, isRecording: true, recordingTime: 0, error: null }, () => {
                setTimeout(() => this.startAudioMonitoring(), 50);
            });
            this.recordingTimer = setInterval(() => {
                this.setState({ recordingTime: this.state.recordingTime + 1 });
            }, 1000);
        } catch (error) {
            console.error('Error starting recording:', error);

            let errorMessage = 'Could not access microphone. ';

            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage += 'Permission denied. Check browser settings and System Preferences > Security & Privacy > Microphone.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += 'No microphone found. Please connect a microphone.';
            } else if (error.name === 'NotReadableError') {
                errorMessage += 'Microphone is already in use by another application.';
            } else {
                errorMessage += error.message;
            }

            this.setState({ isPreparing: false, error: errorMessage });
        }
    }

    stopRecording = () => {
        const { recordingTime } = this.state;

        if (recordingTime < MIN_RECORDING_SECONDS) {
            return;
        }

        if (this.recorder) {
            this.recorder.stopRecording(() => {
                if (this.recorder.stream) {
                    this.recorder.stream.getTracks().forEach(track => track.stop());
                }

                if (this.recordingTimer) {
                    clearInterval(this.recordingTimer);
                    this.recordingTimer = null;
                }

                if (this.animationFrame) {
                    cancelAnimationFrame(this.animationFrame);
                    this.animationFrame = null;
                }
                if (this.audioContext) {
                    this.audioContext.close();
                    this.audioContext = null;
                    this.analyser = null;
                }

                this.setState({ isRecording: false });
                this.transcribeAudio();
            });
        }
    }

    transcribeAudio = async () => {
        const { onTranscription, language = 'no', fieldName } = this.props;

        this.setState({ isTranscribing: true, error: null });

        try {
            const audioBlob = this.recorder.getBlob();
            console.log('RecordRTC blob:', audioBlob?.size, 'bytes, type:', audioBlob?.type);

            if (!audioBlob || audioBlob.size === 0) {
                throw new Error('No audio data recorded');
            }

            const { objectId, objectName } = this.props;

            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.wav');
            formData.append('language', language);
            if (objectId) formData.append('objectId', objectId);
            if (objectName) formData.append('objectName', objectName);
            if (fieldName) formData.append('fieldName', fieldName);

            const token = util.getJwtToken();
            const apiServer = util.getApiServer();

            const fetchResponse = await fetch(`${apiServer}/api/audio/transcribe`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });

            const response = await fetchResponse.json();

            if (response.status === 200) {
                const transcribedText = (response.data?.text || '').trim();

                if (transcribedText) {
                    // Stash raw for the Undo button (cleared when a new
                    // dictation starts or after the undo window expires).
                    this.lastRawText = transcribedText;
                    this.lastRefinedText = null;

                    // Step 1: Insert raw text immediately
                    if (onTranscription) {
                        onTranscription(transcribedText);
                    }

                    // Step 2: Refine in background
                    const refineToken = response.data?.refineToken;
                    if (refineToken) {
                        this.refineTranscription(transcribedText, refineToken);
                    }
                } else {
                    this.setState({
                        error: response.message || 'No speech detected. Try recording a longer message.',
                    });
                }
            } else {
                this.setState({
                    error: response.message || 'Transcription failed',
                });
            }
        } catch (error) {
            console.error('Transcription error:', error);
            this.setState({ error: error.message || 'Transcription failed' });
        } finally {
            this.setState({ isTranscribing: false });
        }
    }

    refineTranscription = async (rawText, refineToken) => {
        const { onTranscription, fieldName, objectName } = this.props;

        this.setState({ isRefining: true, refineStatus: 'refining' });

        try {
            const token = util.getJwtToken();
            const apiServer = util.getApiServer();

            const fetchResponse = await fetch(`${apiServer}/api/audio/refine`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: rawText,
                    fieldName: fieldName || '',
                    objectName: objectName || '',
                    refineToken,
                }),
            });

            const response = await fetchResponse.json();

            let undoable = false;
            if (response.status === 200) {
                const refinedText = (response.data?.text || '').trim();
                const filtered = !!response.data?.filtered;

                if (filtered && onTranscription) {
                    // Field-specific prompt filtered all content as irrelevant.
                    // Clear the dictated text from the field.
                    onTranscription('', { replace: rawText });
                    this.lastRefinedText = '';
                    this.setState({ refineStatus: 'filtered' });
                    undoable = true;
                } else if (refinedText && refinedText !== rawText && onTranscription) {
                    onTranscription(refinedText, { replace: rawText });
                    this.lastRefinedText = refinedText;
                    this.setState({ refineStatus: 'refined' });
                    undoable = true;
                } else {
                    // Refined text identical to raw — nothing to undo
                    this.setState({ refineStatus: 'refined' });
                }
            } else {
                this.setState({ refineStatus: 'failed' });
            }

            // Schedule auto-dismiss: keep undo affordance for 30s when
            // there's something to revert, otherwise clear quickly.
            this.scheduleStatusClear(undoable ? 30000 : 2000);
        } catch (error) {
            console.error('Refine error:', error);
            this.setState({ refineStatus: 'failed' });
            this.scheduleStatusClear(2000);
        } finally {
            this.setState({ isRefining: false });
        }
    }

    scheduleStatusClear = (delayMs) => {
        if (this.refineStatusTimer) {
            clearTimeout(this.refineStatusTimer);
        }
        this.refineStatusTimer = setTimeout(() => {
            this.clearUndo();
        }, delayMs);
    }

    clearUndo = () => {
        if (this.refineStatusTimer) {
            clearTimeout(this.refineStatusTimer);
            this.refineStatusTimer = null;
        }
        this.lastRawText = null;
        this.lastRefinedText = null;
        this.setState({ refineStatus: null });
    }

    undoRefine = () => {
        const { onTranscription } = this.props;
        if (!onTranscription || this.lastRawText == null) {
            this.clearUndo();
            return;
        }

        const { refineStatus } = this.state;
        if (refineStatus === 'filtered') {
            // Field was cleared — re-append the raw transcription.
            onTranscription(this.lastRawText);
        } else if (refineStatus === 'refined' && this.lastRefinedText) {
            // Refined replaced raw — swap it back to raw.
            onTranscription(this.lastRawText, { replace: this.lastRefinedText });
        }
        this.clearUndo();
    }

    formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    }

    componentWillUnmount() {
        if (this.recordingTimer) {
            clearInterval(this.recordingTimer);
        }
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        if (this.audioContext) {
            this.audioContext.close();
        }
        if (this.recorder) {
            this.recorder.destroy();
            this.recorder = null;
        }
        if (this.refineStatusTimer) {
            clearTimeout(this.refineStatusTimer);
            this.refineStatusTimer = null;
        }
    }

    render() {
        const { isPreparing, isRecording, isTranscribing, error, recordingTime, availableDevices, selectedDeviceId, showDeviceSelector } = this.state;
        const canStop = recordingTime >= MIN_RECORDING_SECONDS;
        const countdown = MIN_RECORDING_SECONDS - recordingTime;

        if (isPreparing) {
            return (
                <button type='button' class='btn btn-warning rounded-pill' disabled
                    style='min-height: 44px;'
                >
                    <i class='fa-solid fa-spinner fa-spin me-1' />
                    <Text id='form.preparing-mic'>Klargjør mikrofon...</Text>
                </button>
            );
        }

        if (isTranscribing) {
            return (
                <button type='button' class='btn btn-secondary rounded-pill' disabled
                    style='min-height: 44px;'
                >
                    <i class='fa-solid fa-spinner fa-spin me-1' />
                    <Text id='form.transcribing'>Transkriberer...</Text>
                </button>
            );
        }

        if (isRecording) {
            return (
                <div class='d-flex align-items-center gap-2'>
                    <button
                        type='button'
                        class={`btn rounded-pill ${canStop ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={this.stopRecording}
                        disabled={!canStop}
                        style='min-height: 44px;'
                    >
                        <i class={`fa-solid ${canStop ? 'fa-stop' : 'fa-microphone'} me-1`} />
                        {!canStop && <span>{countdown}s</span>}
                        {canStop && <span class='d-none d-sm-inline'><Text id='form.stop-recording'>Stopp</Text></span>}
                        {canStop && <span class='ms-1 badge bg-light text-dark'>{this.formatTime(recordingTime)}</span>}
                    </button>
                    {!canStop && <small class='text-body-secondary'><Text id='form.speak-now'>Snakk nå...</Text></small>}
                    <canvas
                        ref={c => this.canvasRef = c}
                        width={EQ_WIDTH}
                        height={EQ_HEIGHT}
                        style='display: block;'
                    />
                    {error && (
                        <small class='text-danger'>{error}</small>
                    )}
                </div>
            );
        }

        const selectedDevice = availableDevices.find(d => d.deviceId === selectedDeviceId);
        const selectedDeviceName = selectedDevice?.label || 'Default';

        return (
            <div class='d-flex align-items-center gap-2 flex-wrap'>
                <button
                    type='button'
                    class='btn btn-outline-primary rounded-pill'
                    onClick={this.startRecording}
                    title='Record audio to text'
                    style='min-height: 44px;'
                >
                    <i class='fa-solid fa-microphone me-1' />
                    <span class='d-none d-sm-inline'><Text id='form.dictate'>Dikter</Text></span>
                </button>

                {availableDevices.length > 1 && (
                    <div class='dropdown'>
                        <button
                            type='button'
                            class='btn btn-outline-secondary rounded-pill'
                            onClick={() => this.setState({ showDeviceSelector: !showDeviceSelector })}
                            title={`Select microphone (current: ${selectedDeviceName})`}
                            style='min-height: 44px;'
                        >
                            <i class='fa-solid fa-cog' />
                        </button>
                        {showDeviceSelector && (
                            <div class='dropdown-menu show' style={{ position: 'absolute', zIndex: 1000 }}>
                                {availableDevices.map((device, index) => (
                                    <button
                                        key={device.deviceId}
                                        type='button'
                                        class={`dropdown-item ${device.deviceId === selectedDeviceId ? 'active' : ''}`}
                                        onClick={() => {
                                            localStorage.setItem('audioRecorder_selectedDeviceId', device.deviceId);
                                            this.setState({
                                                selectedDeviceId: device.deviceId,
                                                showDeviceSelector: false
                                            });
                                        }}
                                    >
                                        {device.label || `Microphone ${index + 1}`}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <small class='text-danger'>{error}</small>
                )}
                {this.state.refineStatus === 'refining' && (
                    <small class='text-warning'>
                        <i class='fa-solid fa-spinner fa-spin me-1' />
                        Forbedrer tekst...
                    </small>
                )}
                {this.state.refineStatus === 'refined' && (
                    <small class='text-success'>
                        <i class='fa-solid fa-check me-1' />
                        Tekst forbedret
                    </small>
                )}
                {this.state.refineStatus === 'filtered' && (
                    <small class='text-body-secondary'>
                        <i class='fa-solid fa-filter me-1' />
                        Ingen relevant tekst for feltet
                    </small>
                )}
                {this.state.refineStatus === 'failed' && (
                    <small class='text-body-secondary'>
                        Kunne ikke forbedre tekst
                    </small>
                )}
                {(this.state.refineStatus === 'refined' || this.state.refineStatus === 'filtered')
                    && this.lastRawText && (
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-secondary rounded-pill py-0'
                        onClick={this.undoRefine}
                        title='Gjenopprett original diktering'
                    >
                        <i class='fa-solid fa-rotate-left me-1' />
                        <Text id='form.undo-refine'>Angre forbedring</Text>
                    </button>
                )}
            </div>
        );
    }
}

export default AudioRecorder;
